import { useState, useCallback, useEffect, useRef } from "react";
import { supabase, supabaseUrl, supabaseAnonKey } from "./supabase";

const BRAND="#6B2737", BRAND_LIGHT="#F5EEF0";
const CREAM="#FAFAF8", WHITE="#FFFFFF";
const BORDER="#E5E0D8", BORDER_LIGHT="#F0EBE5";
const TEXT="#1C1C1E", TEXT_MID="#4A4A4F", TEXT_LIGHT="#8E8E93";
const GREEN="#1C7C4A", AMBER="#B45309";
const SANS=`-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif`;

const TOWN_COORDS = {
  "Armonk":{lat:41.1337,lng:-73.7201},"Bronx":{lat:40.8448,lng:-73.8648},
  "Bronxville":{lat:40.9387,lng:-73.8326},"Dobbs Ferry":{lat:41.0104,lng:-73.8746},
  "Eastchester":{lat:40.9579,lng:-73.8118},"Greenburgh":{lat:41.0220,lng:-73.8596},
  "Greenwich":{lat:41.0262,lng:-73.6282},"Harrison":{lat:40.9676,lng:-73.7132},
  "Hastings-on-Hudson":{lat:40.9915,lng:-73.8793},"Larchmont":{lat:40.9287,lng:-73.7529},
  "Mamaroneck":{lat:40.9468,lng:-73.7329},"Mount Vernon":{lat:40.9126,lng:-73.8371},
  "New Rochelle":{lat:40.9115,lng:-73.7826},"Pelham":{lat:40.9065,lng:-73.8082},
  "Pelham Manor":{lat:40.8993,lng:-73.8046},"Port Chester":{lat:40.9887,lng:-73.6668},
  "Rye":{lat:40.9815,lng:-73.6871},"Scarsdale":{lat:40.9890,lng:-73.7846},
  "Sleepy Hollow":{lat:41.0898,lng:-73.8618},"Stamford":{lat:41.0534,lng:-73.5387},
  "Tuckahoe":{lat:40.9504,lng:-73.8243},"White Plains":{lat:41.0340,lng:-73.7629},
  "Yonkers":{lat:40.9312,lng:-73.8988},
};
const TOWNS = Object.keys(TOWN_COORDS).sort();
const DEFAULT_CUISINES = ["American","Bakery","Chinese","French","Indian","Italian","Japanese","Mediterranean","Mexican","Pizza","Seafood","Steakhouse","Sushi","Thai","Other"];
const DEFAULT_TAGS = ["Tacos","Sushi","Burgers","Pub & Tavern","Pizza Parlor","Seafood","BBQ","Cocktails","Ice Cream","Grab & Go Lunch","Fine Dining","Diner & Coffee Shop","Bakery"];
const PJ_LABELS = {0:"Not Yet Rated",1:"Don't Bother",2:"If Convenient",3:"Go, Now!"};

const haversine=(a,b,c,d)=>{const R=3958.8,dL=(c-a)*Math.PI/180,dl=(d-b)*Math.PI/180,x=Math.sin(dL/2)**2+Math.cos(a*Math.PI/180)*Math.cos(c*Math.PI/180)*Math.sin(dl/2)**2;return R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x));};
const nearestTown=(lat,lng)=>{let best=null,bestD=Infinity;for(const[t,c]of Object.entries(TOWN_COORDS)){const d=haversine(lat,lng,c.lat,c.lng);if(d<bestD){bestD=d;best=t;}}return best;};
const townDist=(t1,t2)=>{const c1=TOWN_COORDS[t1],c2=TOWN_COORDS[t2];if(!c1||!c2)return 9999;return haversine(c1.lat,c1.lng,c2.lat,c2.lng);};
const similarTown=(input,towns)=>{const lower=input.toLowerCase().trim();const dist=(a,b)=>{const m=a.length,n=b.length,dp=Array.from({length:m+1},(_,i)=>Array.from({length:n+1},(_,j)=>j===0?i:i===0?j:0));for(let i=1;i<=m;i++)for(let j=1;j<=n;j++)dp[i][j]=a[i-1]===b[j-1]?dp[i-1][j-1]:1+Math.min(dp[i-1][j],dp[i][j-1],dp[i-1][j-1]);return dp[m][n];};return towns.find(t=>t.toLowerCase()!==lower&&dist(lower,t.toLowerCase())<=3);};
const avgRating=revs=>{const p=revs.filter(r=>r.isPublic);return p.length?Math.round(p.reduce((s,r)=>s+r.rating,0)/p.length):0;};

// ── DB mappers ────────────────────────────────────────────────────────────────
const mapRestaurant=r=>({id:r.id,name:r.name,town:r.town||"",cuisine:r.cuisine||"",tags:r.tags||[],address:r.address||"",phone:r.phone||"",website:r.website||"",menu:r.menu_url||"",reservation:r.reservation_url||"",orderOnline:r.order_online_url||"",addedBy:r.added_by||""});
const toDbRestaurant=(r,addedBy="")=>({id:r.id,name:r.name,town:r.town||"",cuisine:r.cuisine||"",tags:r.tags||[],address:r.address||"",phone:r.phone||"",website:r.website||"",menu_url:r.menu||"",reservation_url:r.reservation||"",order_online_url:r.orderOnline||"",added_by:r.addedBy||addedBy});
const mapProfile=p=>({id:p.id,username:p.username,name:p.name,email:p.email,isAdmin:p.is_admin,approved:p.approved});
const mapReview=r=>({id:r.id,restaurantId:r.restaurant_id,userId:r.user_id,username:r.username,name:r.name,rating:r.rating,review:r.review,isPublic:r.is_public});
const toDbReview=r=>({id:r.id,restaurant_id:r.restaurantId,user_id:r.userId,username:r.username,name:r.name,rating:r.rating,review:r.review,is_public:r.isPublic});

// ── AI autofill (routed through Edge Function — fixes CORS) ───────────────────
const aiPopulate=async(query,cuisines)=>{
  try{
    const {data:{session}}=await supabase.auth.getSession();
    const token=session?.access_token??supabaseAnonKey;
    const resp=await fetch(`${supabaseUrl}/functions/v1/ai-populate`,{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":supabaseAnonKey},body:JSON.stringify({query,cuisines,tags:DEFAULT_TAGS})});
    if(!resp.ok){console.error("ai-populate HTTP error:",resp.status);return null;}
    const data=await resp.json();
    if(!data){console.error("ai-populate: no data returned");return null;}
    if(data.type==="error"){console.error("ai-populate: Anthropic API error:",data.error);return null;}
    const text=(data.content||[]).map(b=>b.type==="text"?b.text:"").filter(Boolean).join("");
    const match=text.match(/\{[\s\S]*\}/);
    if(!match){console.error("ai-populate: no JSON found in response. Content:",JSON.stringify(data.content));return null;}
    return JSON.parse(match[0]);
  }catch(e){console.error("ai-populate exception:",e);return null;}
};

// ── Design tokens ─────────────────────────────────────────────────────────────
const T={
  inp:{width:"100%",background:WHITE,border:`1.5px solid ${BORDER}`,borderRadius:8,padding:"12px 14px",color:TEXT,fontSize:15,marginBottom:12,boxSizing:"border-box",outline:"none",fontFamily:SANS,WebkitAppearance:"none",display:"block",boxShadow:"0 1px 2px rgba(0,0,0,0.04)"},
  sel:{background:WHITE,border:`1.5px solid ${BORDER}`,borderRadius:8,padding:"10px 12px",color:TEXT,fontSize:13,outline:"none",fontFamily:SANS,WebkitAppearance:"none",width:"100%",boxShadow:"0 1px 2px rgba(0,0,0,0.04)"},
  btnP:{width:"100%",background:BRAND,border:"none",borderRadius:8,padding:"13px",color:WHITE,fontWeight:700,fontSize:15,cursor:"pointer",marginTop:4,fontFamily:SANS,boxShadow:`0 2px 8px rgba(107,39,55,0.25)`},
  btnO:{width:"100%",background:"none",border:`1.5px solid ${BORDER}`,borderRadius:8,padding:"13px",color:TEXT_MID,fontWeight:600,fontSize:14,cursor:"pointer",marginTop:8,fontFamily:SANS},
  card:{background:WHITE,margin:"8px 14px",borderRadius:12,padding:"16px 18px",border:`1px solid ${BORDER}`,boxShadow:"0 1px 6px rgba(0,0,0,0.06)"},
  lbl:{fontSize:10,fontWeight:700,color:TEXT_LIGHT,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:SANS,marginBottom:6,display:"block"},
};
const Label=({children,style={}})=><div style={{...T.lbl,...style}}>{children}</div>;
const Stars=({n,max=3,size=18,interactive=false,onChange})=>(
  <span style={{display:"inline-flex",gap:1}}>
    {Array.from({length:max},(_,i)=>(
      <span key={i} onClick={interactive?()=>onChange(i+1):undefined}
        style={{fontSize:size,color:BRAND,cursor:interactive?"pointer":"default",lineHeight:1,display:"inline-block"}}>
        {i<n?"★":"☆"}
      </span>
    ))}
  </span>
);
const Modal=({children,onClose})=>(
  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={onClose}>
    <div style={{background:WHITE,borderRadius:14,padding:24,maxWidth:340,width:"100%",boxShadow:"0 16px 48px rgba(0,0,0,0.25)",maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
      {children}
    </div>
  </div>
);
const TagPill=({label,selected,onClick})=>(
  <button onClick={onClick} style={{background:selected?BRAND:WHITE,border:`1.5px solid ${selected?BRAND:BORDER}`,borderRadius:20,padding:"5px 12px",color:selected?WHITE:TEXT_MID,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:SANS,whiteSpace:"nowrap",lineHeight:1.4}}>
    {label}
  </button>
);
const FunnelIcon=()=><svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}><path d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A1 1 0 0018.95 4H5.04a1 1 0 00-.79 1.61z"/></svg>;
const SortIcon=()=><svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}><path d="M7 10l5 5 5-5z"/></svg>;

// ── Restaurant Form ───────────────────────────────────────────────────────────
const RestaurantForm=({initial,onSave,onCancel,onDelete,allCuisines,customTowns,addCustomTown,addCustomCuisine,isEdit=false})=>{
  const allTowns=[...TOWNS,...customTowns].sort();
  const [query,setQuery]=useState("");
  const [loading,setLoading]=useState(false);
  const [aiMsg,setAiMsg]=useState("");
  const [form,setForm]=useState({name:"",town:"",cuisine:"",tags:[],address:"",phone:"",website:"",menu:"",reservation:"",orderOnline:"",...(initial||{})});
  const [showForm,setShowForm]=useState(!!isEdit);
  const [customTown,setCustomTown]=useState(""); const [showCustomTown,setShowCustomTown]=useState(false); const [townSuggest,setTownSuggest]=useState(null);
  const [newCuisine,setNewCuisine]=useState(""); const [showNewCuisine,setShowNewCuisine]=useState(false);
  const [confirmDelete,setConfirmDelete]=useState(false);

  const f=(k,v)=>setForm(p=>({...p,[k]:v}));
  const toggleTag=t=>setForm(p=>({...p,tags:(p.tags||[]).includes(t)?(p.tags||[]).filter(x=>x!==t):[...(p.tags||[]),t]}));

  const populate=async()=>{
    if(!query)return;setLoading(true);setAiMsg("Searching for restaurant…");
    const data=await aiPopulate(query,allCuisines);
    if(data){setForm(p=>({...p,...data,tags:data.tags||[]}));setAiMsg("✓ Info filled in — please review and complete any missing fields.");}
    else setAiMsg("Couldn't find that restaurant. Please enter details manually below.");
    setShowForm(true);setLoading(false);
  };
  const handleCustomTown=()=>{
    const val=customTown.trim();if(!val)return;
    const match=similarTown(val,allTowns);if(match){setTownSuggest(match);return;}
    addCustomTown(val);f("town",val);setShowCustomTown(false);setCustomTown("");
  };
  const handleAddCuisine=()=>{
    const val=newCuisine.trim();if(!val)return;
    addCustomCuisine(val);f("cuisine",val);setShowNewCuisine(false);setNewCuisine("");
  };

  return(
    <div style={{paddingBottom:120,fontFamily:SANS}}>
      {confirmDelete&&<Modal onClose={()=>setConfirmDelete(false)}>
        <div style={{fontWeight:800,fontSize:17,marginBottom:8,color:TEXT}}>Delete Restaurant?</div>
        <div style={{fontSize:14,color:TEXT_MID,marginBottom:20,lineHeight:1.6}}>Are you sure you want to permanently delete <strong>{form.name}</strong>? All ratings and reviews for this restaurant will also be removed.</div>
        <div style={{display:"flex",gap:10}}>
          <button style={{...T.btnP,background:"#C0392B",flex:1,boxShadow:"none"}} onClick={onDelete}>Delete Permanently</button>
          <button style={{...T.btnO,flex:1,marginTop:0}} onClick={()=>setConfirmDelete(false)}>Cancel</button>
        </div>
      </Modal>}
      <div style={{background:WHITE,padding:"20px 18px",borderBottom:`1px solid ${BORDER}`}}>
        <button style={{background:"none",border:"none",color:BRAND,fontSize:14,cursor:"pointer",padding:"0 0 12px",fontWeight:700,fontFamily:SANS}} onClick={onCancel}>← Back</button>
        <div style={{fontWeight:800,fontSize:24,color:TEXT}}>{isEdit?"Edit Restaurant":"Add a Restaurant"}</div>
        {!isEdit&&<div style={{fontSize:13,color:TEXT_LIGHT,marginTop:4}}>Search by restaurant name to auto-fill details.</div>}
      </div>
      <div style={{padding:"20px 16px"}}>
        {!isEdit&&<>
          <Label>Restaurant Name</Label>
          <div style={{display:"flex",gap:8,marginBottom:4}}>
            <input style={{...T.inp,marginBottom:0,flex:1}} placeholder="e.g. Rye Roadhouse, Larchmont" value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&populate()} autoCapitalize="words" autoCorrect="off" spellCheck={false}/>
            <button style={{...T.btnP,width:"auto",padding:"0 18px",marginTop:0,flexShrink:0}} onClick={populate} disabled={loading}>{loading?"…":"Search"}</button>
          </div>
          {aiMsg&&<div style={{fontSize:13,color:aiMsg.startsWith("✓")?GREEN:AMBER,margin:"12px 0",padding:"10px 14px",background:aiMsg.startsWith("✓")?"#F0FAF4":"#FFFBF0",borderRadius:8,border:`1px solid ${aiMsg.startsWith("✓")?"#A8D5B8":"#E8D5A0"}`}}>{aiMsg}</div>}
          {!showForm&&<button style={{...T.btnO,marginTop:14}} onClick={()=>setShowForm(true)}>Enter Manually Instead</button>}
        </>}
        {showForm&&<div style={{marginTop:isEdit?0:16,borderTop:isEdit?"none":`1px solid ${BORDER}`,paddingTop:isEdit?0:16}}>
          {[["name","Restaurant Name *"],["address","Full Address"],["phone","Phone Number"],["website","Website URL"],["menu","Menu URL"],["reservation","Reservation URL"],["orderOnline","Order Online URL"]].map(([k,pl])=>(
            <div key={k}><Label>{pl}</Label><input style={T.inp} placeholder={pl} value={form[k]||""} onChange={e=>f(k,e.target.value)}/></div>
          ))}
          <Label>Town</Label>
          {!showCustomTown?(
            <select style={{...T.sel,marginBottom:12}} value={form.town} onChange={e=>{if(e.target.value==="__custom__")setShowCustomTown(true);else f("town",e.target.value);}}>
              <option value="">Select Town</option>{allTowns.map(t=><option key={t}>{t}</option>)}<option value="__custom__">+ Add a town not listed…</option>
            </select>
          ):(<>
            <input style={T.inp} placeholder="Town name" value={customTown} onChange={e=>{setCustomTown(e.target.value);setTownSuggest(null);}}/>
            {townSuggest&&<div style={{background:"#FFFDF0",border:`1px solid ${AMBER}44`,borderRadius:8,padding:"12px 14px",fontSize:13,marginBottom:10}}>
              <div style={{marginBottom:8}}>Did you mean <strong>{townSuggest}</strong>?</div>
              <div style={{display:"flex",gap:8}}>
                <button style={{...T.btnP,width:"auto",padding:"7px 16px",fontSize:13}} onClick={()=>{f("town",townSuggest);setShowCustomTown(false);setCustomTown("");setTownSuggest(null);}}>Yes, use {townSuggest}</button>
                <button style={{...T.btnO,width:"auto",padding:"7px 16px",fontSize:13,marginTop:0}} onClick={()=>setTownSuggest(null)}>No, it's new</button>
              </div>
            </div>}
            {!townSuggest&&<div style={{display:"flex",gap:8,marginBottom:12}}>
              <button style={{...T.btnP,width:"auto",padding:"9px 18px",fontSize:13}} onClick={handleCustomTown}>Add Town</button>
              <button style={{...T.btnO,width:"auto",padding:"9px 18px",fontSize:13,marginTop:0}} onClick={()=>{setShowCustomTown(false);setCustomTown("");}}>Cancel</button>
            </div>}
          </>)}
          <Label>Cuisine</Label>
          {!showNewCuisine?(
            <select style={{...T.sel,marginBottom:8}} value={form.cuisine} onChange={e=>{if(e.target.value==="__new__")setShowNewCuisine(true);else f("cuisine",e.target.value);}}>
              <option value="">Select Cuisine</option>{allCuisines.map(c=><option key={c}>{c}</option>)}<option value="__new__">+ Add a new cuisine…</option>
            </select>
          ):<>
            <input style={T.inp} placeholder="New cuisine name" value={newCuisine} onChange={e=>setNewCuisine(e.target.value)}/>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <button style={{...T.btnP,width:"auto",padding:"9px 18px",fontSize:13}} onClick={handleAddCuisine}>Add Cuisine</button>
              <button style={{...T.btnO,width:"auto",padding:"9px 18px",fontSize:13,marginTop:0}} onClick={()=>{setShowNewCuisine(false);setNewCuisine("");}}>Cancel</button>
            </div>
          </>}
          <Label style={{marginTop:8}}>Tags (select all that apply)</Label>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
            {DEFAULT_TAGS.map(t=><TagPill key={t} label={t} selected={(form.tags||[]).includes(t)} onClick={()=>toggleTag(t)}/>)}
          </div>
          <button style={T.btnP} onClick={()=>onSave(form)}>{isEdit?"Save Changes":"Add to The Regular"}</button>
          {isEdit&&(
            <button onClick={()=>setConfirmDelete(true)} style={{width:"100%",marginTop:20,background:"none",border:`1.5px solid #F5C6CB`,borderRadius:8,padding:"13px",color:"#C0392B",fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:SANS}}>
              Delete Restaurant
            </button>
          )}
        </div>}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [appLoading,setAppLoading]=useState(true);
  const [user,setUser]=useState(null);
  const [screen,setScreen]=useState("login");
  const [tab,setTab]=useState("restaurants");
  const [restaurants,setRestaurants]=useState([]);
  const [users,setUsers]=useState([]);
  const [pjReviews,setPjReviews]=useState({});
  const [jessicaReviews,setJessicaReviews]=useState({});
  const [userReviews,setUserReviews]=useState([]);
  const [pending,setPending]=useState([]);
  const [favIds,setFavIds]=useState([]);
  const [customCuisines,setCustomCuisines]=useState([]);
  const [customTowns,setCustomTowns]=useState([]);
  const [selRest,setSelRest]=useState(null);
  const [selUser,setSelUser]=useState(null);
  const [showAdd,setShowAdd]=useState(false);
  const [editRest,setEditRest]=useState(null);
  const [showProfile,setShowProfile]=useState(false);
  const [filterTown,setFilterTown]=useState("");
  const [filterCuisine,setFilterCuisine]=useState("");
  const [filterTags,setFilterTags]=useState([]);
  const [filterSearch,setFilterSearch]=useState("");
  const searchRef=useRef(null);
  const [openPill,setOpenPill]=useState(null);
  const [sortBy,setSortBy]=useState("name");
  const [userTown,setUserTown]=useState(null);
  const [locationModal,setLocationModal]=useState(false);
  const [resetModal,setResetModal]=useState(false);
  const [newPassword,setNewPassword]=useState("");
  const [resetPwErr,setResetPwErr]=useState("");
  const [prevSort,setPrevSort]=useState("name");

  const allCuisines=[...DEFAULT_CUISINES,...customCuisines].sort();
  const shareUrl=typeof window!=="undefined"?window.location.href:"";

  // ── Auth state listener ───────────────────────────────────────────────
  useEffect(()=>{
    // Resolve initial state immediately using getSession() — reliable on every mount
    supabase.auth.getSession().then(({data:{session}})=>{
      if(session) loadUserAndData(session.user.id);
      else setAppLoading(false);
    });

    // Handle transitions after the initial load
    const {data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
      if(event==="PASSWORD_RECOVERY"){
        setResetModal(true);
      } else if(event==="USER_UPDATED"){

        setResetModal(false);setNewPassword("");
      } else if(event==="SIGNED_IN"){
        loadUserAndData(session.user.id);
      } else if(event==="SIGNED_OUT"){
        setUser(null);setScreen("login");
        setRestaurants([]);setUsers([]);setPjReviews({});setJessicaReviews({});
        setUserReviews([]);setPending([]);setFavIds([]);setCustomCuisines([]);setCustomTowns([]);
        setSelRest(null);setSelUser(null);setShowAdd(false);setEditRest(null);setShowProfile(false);
        setAppLoading(false);
      }
    });
    return ()=>subscription.unsubscribe();
  },[]);// eslint-disable-line

  const loadUserAndData=async(userId)=>{
    setAppLoading(true);
    const {data:profile}=await supabase.from("profiles").select("*").eq("id",userId).single();
    if(!profile||!profile.approved){await supabase.auth.signOut();setAppLoading(false);return;}
    setUser(mapProfile(profile));
    await loadAllData(userId);
    setScreen("app");
    setAppLoading(false);
  };

  const loadAllData=async(userId)=>{
    const[
      {data:rData},{data:uData},{data:pjData},{data:jessData},
      {data:notesData},{data:revData},{data:pendData},
      {data:favData},{data:cuisData},{data:townData}
    ]=await Promise.all([
      supabase.from("restaurants").select("*").order("name"),
      supabase.from("profiles").select("*").eq("approved",true),
      supabase.from("pj_ratings").select("*"),
      supabase.from("jessica_ratings").select("*"),
      supabase.from("admin_notes").select("*"),
      supabase.from("user_reviews").select("*"),
      supabase.from("pending_requests").select("*").order("created_at"),
      supabase.from("favorite_members").select("favorited_user_id").eq("user_id",userId),
      supabase.from("custom_cuisines").select("name"),
      supabase.from("custom_towns").select("name"),
    ]);
    setRestaurants((rData||[]).map(mapRestaurant));
    setUsers((uData||[]).map(mapProfile));
    const pjMerged={};
    (pjData||[]).forEach(r=>{pjMerged[r.restaurant_id]={rating:r.rating,review:""};});
    (notesData||[]).forEach(r=>{pjMerged[r.restaurant_id]={...pjMerged[r.restaurant_id]||{rating:0},review:r.note};});
    setPjReviews(pjMerged);
    setJessicaReviews(Object.fromEntries((jessData||[]).map(r=>[r.restaurant_id,{rating:r.rating}])));
    setUserReviews((revData||[]).map(mapReview));
    setPending(pendData||[]);
    setFavIds((favData||[]).map(r=>r.favorited_user_id));
    setCustomCuisines((cuisData||[]).map(r=>r.name));
    setCustomTowns((townData||[]).map(r=>r.name));
  };

  const logout=async()=>{
    setTab("restaurants");
    await supabase.auth.signOut();
  };

  const addCustomCuisine=async(name)=>{
    setCustomCuisines(p=>[...p,name]);
    await supabase.from("custom_cuisines").insert({name});
  };
  const addCustomTown=async(name)=>{
    setCustomTowns(p=>[...p,name]);
    await supabase.from("custom_towns").insert({name});
  };

  // ── LOGIN ────────────────────────────────────────────────────────────
  const LoginScreen=()=>{
    const [u,setU]=useState(""); const [p,setP]=useState(""); const [err,setErr]=useState(""); const [busy,setBusy]=useState(false);
    const [forgotOpen,setForgotOpen]=useState(false); const [forgotEmail,setForgotEmail]=useState(""); const [forgotSent,setForgotSent]=useState(false);
    const login=async()=>{
      setErr("");setBusy(true);
      const {data:email,error:rpcErr}=await supabase.rpc("get_email_for_login",{p_username:u.trim().toLowerCase()});
      if(rpcErr||!email){setErr("Invalid credentials or account not yet approved.");setBusy(false);return;}
      const {error}=await supabase.auth.signInWithPassword({email,password:p});
      if(error)setErr("Invalid credentials or account not yet approved.");
      setBusy(false);
    };
    const sendReset=async()=>{
      await supabase.auth.resetPasswordForEmail(forgotEmail.trim(),{redirectTo:"https://theregular.vercel.app"});
      setForgotSent(true);
    };
    return(
      <div style={{minHeight:"100vh",background:CREAM,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 28px",fontFamily:SANS}}>
        {forgotOpen&&<Modal onClose={()=>{setForgotOpen(false);setForgotEmail("");setForgotSent(false);}}>
          <div style={{fontWeight:800,fontSize:17,marginBottom:8,color:TEXT}}>Reset Password</div>
          {forgotSent
            ?<div style={{fontSize:14,color:GREEN,fontWeight:600,padding:"12px 0"}}>Check your email for a reset link.</div>
            :<><div style={{fontSize:14,color:TEXT_MID,marginBottom:16,lineHeight:1.5}}>Enter your email address and we'll send you a reset link.</div>
              <input style={T.inp} type="email" placeholder="Email address" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} autoFocus/>
              <button style={T.btnP} disabled={!forgotEmail.trim()} onClick={sendReset}>Send Reset Link</button></>}
        </Modal>}
        <div style={{width:"100%",maxWidth:360}}>
          <div style={{textAlign:"center",marginBottom:40}}>
<div style={{fontSize:40,fontWeight:800,color:BRAND,letterSpacing:"0.01em",marginBottom:6}}>The Regular</div>
            <div style={{fontSize:14,color:TEXT_MID,lineHeight:1.7,marginTop:10,textAlign:"center"}}>The Regular is an invite-only restaurant guide created by PJ and Jessica Heyer and curated with help from our members. We moved to Westchester in 2021, but it took a while to feel at home. You may be new here, but you're already a local. You should eat like a Regular. Welcome in.</div>
          </div>
          <Label>Username</Label><input style={T.inp} placeholder="Username" value={u} onChange={e=>setU(e.target.value)} autoCapitalize="none"/>
          <Label>Password</Label><input style={T.inp} placeholder="Password" type="password" value={p} onChange={e=>setP(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()}/>
          {err&&<div style={{color:"#C0392B",fontSize:13,marginBottom:10,padding:"8px 12px",background:"#FDF2F2",borderRadius:6,border:"1px solid #F5C6CB"}}>{err}</div>}
          <button style={T.btnP} onClick={login} disabled={busy}>{busy?"Signing in…":"Sign In"}</button>
          <div style={{textAlign:"center",marginTop:14,marginBottom:4}}><button onClick={()=>setForgotOpen(true)} style={{background:"none",border:"none",color:TEXT_LIGHT,fontSize:13,cursor:"pointer",fontFamily:SANS,textDecoration:"underline",padding:0}}>Forgot password?</button></div>
          <div style={{textAlign:"center",margin:"12px 0 10px",color:TEXT_LIGHT,fontSize:13}}>Don't have access?</div>
          <button style={T.btnO} onClick={()=>setScreen("request")}>Request Access</button>
        </div>
      </div>
    );
  };

  // ── REQUEST ──────────────────────────────────────────────────────────
  const RequestScreen=()=>{
    const [name,setName]=useState(""); const [email,setEmail]=useState(""); const [note,setNote]=useState(""); const [sent,setSent]=useState(false); const [err,setErr]=useState(""); const [busy,setBusy]=useState(false);
    const submit=async()=>{
      if(!name||!email){setErr("Please enter your full name and email address.");return;}
      if(!/\S+@\S+\.\S+/.test(email)){setErr("Please enter a valid email address.");return;}
      setBusy(true);
      const {error}=await supabase.from("pending_requests").insert({id:`req_${Date.now()}`,name,email,note,request_date:new Date().toLocaleDateString()});
      if(error){setErr("Something went wrong. Please try again.");setBusy(false);return;}
      setSent(true);setBusy(false);
    };
    return(
      <div style={{minHeight:"100vh",background:CREAM,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 28px",fontFamily:SANS}}>
        <div style={{width:"100%",maxWidth:360}}>
          <div style={{textAlign:"center",marginBottom:32}}><div style={{fontSize:40,fontWeight:800,color:BRAND}}>The Regular</div></div>
          {sent?(<div style={{textAlign:"center"}}>
            <div style={{width:56,height:56,borderRadius:"50%",background:BRAND_LIGHT,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:24,color:BRAND}}>✓</div>
            <div style={{fontSize:20,fontWeight:700,color:TEXT,marginBottom:8}}>Request Sent</div>
            <div style={{color:TEXT_MID,fontSize:14,lineHeight:1.6,marginBottom:28}}>PJ or Jessica will review your request and reach out at <strong>{email}</strong> once approved.</div>
            <button style={T.btnP} onClick={()=>setScreen("login")}>Back to Sign In</button>
          </div>):(<>
            <Label>Full Name *</Label><input style={T.inp} placeholder="Your full name" value={name} onChange={e=>setName(e.target.value)}/>
            <Label>Email Address *</Label><input style={T.inp} placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)} type="email" autoCapitalize="none"/>
            <Label>How do you know PJ or Jessica? (optional)</Label>
            <textarea style={{...T.inp,minHeight:80,resize:"vertical"}} placeholder="Tell us how you know them…" value={note} onChange={e=>setNote(e.target.value)}/>
            {err&&<div style={{color:"#C0392B",fontSize:13,marginBottom:10,padding:"8px 12px",background:"#FDF2F2",borderRadius:6}}>{err}</div>}
            <button style={T.btnP} onClick={submit} disabled={busy}>{busy?"Sending…":"Submit Request"}</button>
            <button style={T.btnO} onClick={()=>setScreen("login")}>Back to Sign In</button>
          </>)}
        </div>
      </div>
    );
  };

  // ── PROFILE PAGE ─────────────────────────────────────────────────────
  const ProfilePage=()=>{
    const [cur,setCur]=useState(""); const [nw,setNw]=useState(""); const [conf,setConf]=useState(""); const [pwErr,setPwErr]=useState(""); const [pwOk,setPwOk]=useState(false); const [busy,setBusy]=useState(false);
    const myRevs=userReviews.filter(r=>r.userId===user.id);
    const changePassword=async()=>{
      if(nw.length<6){setPwErr("New password must be at least 6 characters.");return;}
      if(nw!==conf){setPwErr("Passwords do not match.");return;}
      setBusy(true);
      // Verify current password by re-authenticating
      const {error:signInErr}=await supabase.auth.signInWithPassword({email:user.email,password:cur});
      if(signInErr){setPwErr("Current password is incorrect.");setBusy(false);return;}
      const {error}=await supabase.auth.updateUser({password:nw});
      if(error){setPwErr(error.message);setBusy(false);return;}
      setPwOk(true);setPwErr("");setBusy(false);
    };
    return(
      <div style={{paddingBottom:100,fontFamily:SANS}}>
        <div style={{background:WHITE,padding:"20px 18px",borderBottom:`1px solid ${BORDER}`}}>
          <button style={{background:"none",border:"none",color:BRAND,fontSize:14,cursor:"pointer",padding:"0 0 14px",fontWeight:700,fontFamily:SANS}} onClick={()=>setShowProfile(false)}>← Back</button>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <div style={{width:56,height:56,borderRadius:"50%",background:BRAND_LIGHT,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill={BRAND}><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
            </div>
            <div>
              <div style={{fontWeight:800,fontSize:22,color:TEXT}}>{user.name}</div>
              <div style={{fontSize:12,color:TEXT_LIGHT,marginTop:2}}>@{user.username} · {myRevs.length} {myRevs.length===1?"review":"reviews"}</div>
            </div>
          </div>
        </div>
        <div style={T.card}>
          <Label>Change Password</Label>
          {pwOk?<div style={{color:GREEN,fontWeight:600,fontSize:14,padding:"8px 0"}}>✓ Password updated successfully.</div>:<>
            <Label>Current Password</Label><input style={T.inp} type="password" value={cur} onChange={e=>setCur(e.target.value)} placeholder="Current password"/>
            <Label>New Password</Label><input style={T.inp} type="password" value={nw} onChange={e=>setNw(e.target.value)} placeholder="At least 6 characters"/>
            <Label>Confirm New Password</Label><input style={T.inp} type="password" value={conf} onChange={e=>setConf(e.target.value)} placeholder="Repeat new password"/>
            {pwErr&&<div style={{color:"#C0392B",fontSize:13,marginBottom:10,padding:"8px 12px",background:"#FDF2F2",borderRadius:6}}>{pwErr}</div>}
            <button style={T.btnP} onClick={changePassword} disabled={busy}>{busy?"Updating…":"Update Password"}</button>
          </>}
        </div>
        <div style={{...T.card,marginTop:4}}>
          <button onClick={logout} style={{...T.btnO,marginTop:0,color:"#C0392B",border:`1.5px solid #F5C6CB`,width:"100%"}}>Log Out</button>
        </div>
      </div>
    );
  };

  // ── INLINE RATING ────────────────────────────────────────────────────
  const InlineRating=({restaurantId,who})=>{
    const isPJ=who==="pj"; const isJessica=who==="jessica"; const isAdminRating=isPJ||isJessica;
    const canEdit=isPJ?(user.username==="pj"):isJessica?(user.username==="jessica"):true;
    const getEx=useCallback(()=>{
      if(isPJ)return pjReviews[restaurantId]||{rating:0};
      if(isJessica)return jessicaReviews[restaurantId]||{rating:0};
      return userReviews.find(x=>x.restaurantId===restaurantId&&x.userId===user.id)||null;
    },[restaurantId]);// eslint-disable-line
    const ex=getEx(); const [rating,setRating]=useState(ex?.rating||0); const [review,setReview]=useState(ex?.review||""); const [isPublic,setIsPublic]=useState(ex?.isPublic??true);
    const doSave=useCallback(async(r,rv,ip)=>{
      if(isPJ){
        setPjReviews(p=>({...p,[restaurantId]:{...p[restaurantId],rating:r}}));
        await supabase.from("pj_ratings").upsert({restaurant_id:restaurantId,rating:r,updated_at:new Date().toISOString()});
      } else if(isJessica){
        setJessicaReviews(p=>({...p,[restaurantId]:{rating:r}}));
        await supabase.from("jessica_ratings").upsert({restaurant_id:restaurantId,rating:r,updated_at:new Date().toISOString()});
      } else {
        const ex2=userReviews.find(x=>x.restaurantId===restaurantId&&x.userId===user.id);
        const base={id:ex2?.id||`rev_${Date.now()}`,restaurantId,userId:user.id,username:user.username,name:user.name,rating:r,review:rv,isPublic:ip};
        setUserReviews(p=>ex2?p.map(x=>x.id===ex2.id?base:x):[...p,base]);
        await supabase.from("user_reviews").upsert(toDbReview(base));
      }
    },[restaurantId,who]);// eslint-disable-line
    return(
      <div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:isAdminRating?0:10}}>
          <Stars n={rating} size={28}/>
          {rating>0?<span style={{fontSize:12,color:TEXT_MID,fontWeight:600}}>{PJ_LABELS[rating]}</span>:<span style={{fontSize:12,color:TEXT_LIGHT}}>Not yet rated</span>}
          {canEdit&&<select value={rating} onChange={e=>{const n=+e.target.value;setRating(n);doSave(n,review,isPublic);}} style={{marginLeft:"auto",background:BRAND_LIGHT,border:`1.5px solid ${BRAND}44`,borderRadius:20,padding:"5px 14px",color:BRAND,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:SANS,outline:"none",appearance:"none",WebkitAppearance:"none"}}>
            <option value={0}>— No rating</option>
            <option value={1}>1 — Don't Bother</option>
            <option value={2}>2 — If Convenient</option>
            <option value={3}>3 — Go, Now!</option>
          </select>}
          {!isAdminRating&&rating>0&&<label style={{fontSize:12,color:TEXT_MID,display:"flex",alignItems:"center",gap:5,cursor:"pointer"}}><input type="checkbox" checked={isPublic} onChange={e=>{setIsPublic(e.target.checked);doSave(rating,review,e.target.checked);}} style={{accentColor:BRAND}}/>Public</label>}
        </div>
        {!isAdminRating&&<><textarea style={{...T.inp,minHeight:90,resize:"vertical",marginBottom:4,fontSize:13,lineHeight:1.5}} placeholder="Your review and notes. The most helpful notes will reference specific menu items or tips about the restaurant." value={review} onChange={e=>setReview(e.target.value)} onBlur={e=>doSave(rating,e.target.value,isPublic)}/><div style={{fontSize:11,color:TEXT_LIGHT,textAlign:"right"}}>Saved automatically</div></>}
      </div>
    );
  };

  // ── ADMIN NOTES ───────────────────────────────────────────────────────
  const AdminNotes=({restaurantId})=>{
    const ex=pjReviews[restaurantId]||{review:""};
    const [note,setNote]=useState(ex.review||"");
    const doSave=useCallback(async(v)=>{
      setPjReviews(p=>({...p,[restaurantId]:{...p[restaurantId],review:v}}));
      await supabase.from("admin_notes").upsert({restaurant_id:restaurantId,note:v,updated_at:new Date().toISOString()});
    },[restaurantId]);// eslint-disable-line
    if(!user.isAdmin)return note?<div style={{fontSize:14,color:TEXT_MID,fontStyle:"italic",lineHeight:1.6}}>"{note}"</div>:<div style={{fontSize:13,color:TEXT_LIGHT}}>No notes yet.</div>;
    return(
      <div>
        <textarea style={{...T.inp,minHeight:90,resize:"vertical",marginBottom:4,fontSize:13,lineHeight:1.5}} placeholder="Shared notes from PJ and Jessica…" value={note} onChange={e=>setNote(e.target.value)} onBlur={e=>doSave(e.target.value)}/>
        <div style={{fontSize:11,color:TEXT_LIGHT,textAlign:"right"}}>Saved automatically</div>
      </div>
    );
  };

  // ── RESTAURANT LIST ──────────────────────────────────────────────────
  const RestaurantList=()=>{
    const getRevs=id=>userReviews.filter(r=>r.restaurantId===id&&r.isPublic);
    const pjR=id=>pjReviews[id]||{rating:0};
    const jessR=id=>jessicaReviews[id]||{rating:0};
    const allTowns=[...TOWNS,...customTowns].sort();
    const handleSort=val=>{if(val==="distance"){setPrevSort(sortBy);setLocationModal(true);}else setSortBy(val);};
    const toggleTag=t=>setFilterTags(p=>p.includes(t)?p.filter(x=>x!==t):[...p,t]);
    const sortKey=n=>n.replace(/^(The|An?)\s+/i,"");
    let list=restaurants.filter(r=>{
      if(filterTown&&r.town!==filterTown)return false;
      if(filterCuisine&&r.cuisine!==filterCuisine)return false;
      if(filterTags.length>0&&!filterTags.every(t=>(r.tags||[]).includes(t)))return false;
      return true;
    });
    list=[...list].sort((a,b)=>{
      if(sortBy==="pj")return pjR(b.id).rating-pjR(a.id).rating;
      if(sortBy==="members")return avgRating(getRevs(b.id))-avgRating(getRevs(a.id));
      if(sortBy==="distance"&&userTown)return townDist(userTown,a.town)-townDist(userTown,b.town);
      return sortKey(a.name).localeCompare(sortKey(b.name));
    });
    if(filterSearch)list=list.filter(r=>r.name.toLowerCase().includes(filterSearch.toLowerCase()));
    const ALPHA="ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const letterFirstId={};
    list.forEach(r=>{const l=sortKey(r.name)[0]?.toUpperCase();if(l&&!letterFirstId[l])letterFirstId[l]=r.id;});
    const scrollToLetter=letter=>{const id=letterFirstId[letter];if(id)document.getElementById(`card-${id}`)?.scrollIntoView({behavior:"smooth",block:"start"});};
    const azRef=useRef(null);const azTimer=useRef(null);
    const azShow=()=>{clearTimeout(azTimer.current);if(azRef.current)azRef.current.style.opacity="1";};
    const azHide=(ms=0)=>{clearTimeout(azTimer.current);azTimer.current=setTimeout(()=>{if(azRef.current)azRef.current.style.opacity="0";},ms);};
    return(
      <div style={{paddingBottom:160,fontFamily:SANS}}>
        {locationModal&&<Modal onClose={()=>{setLocationModal(false);setSortBy(prevSort);}}>
          <div style={{fontWeight:800,fontSize:17,marginBottom:8,color:TEXT}}>Share Your Location?</div>
          <div style={{fontSize:14,color:TEXT_MID,lineHeight:1.6,marginBottom:20}}>The Regular will use your current location to find your nearest town and sort restaurants by distance.</div>
          <button style={T.btnP} onClick={()=>{setLocationModal(false);navigator.geolocation?.getCurrentPosition(pos=>{setUserTown(nearestTown(pos.coords.latitude,pos.coords.longitude));setSortBy("distance");},()=>setSortBy(prevSort));}}>Share Once</button>
          <button style={T.btnO} onClick={()=>{setLocationModal(false);setSortBy(prevSort);}}>Don't Share</button>
        </Modal>}
        <div style={{background:WHITE,borderBottom:`1px solid ${BORDER}`,padding:"10px 14px"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
            {[
              {id:"towns",label:"Towns",active:!!filterTown,icon:<FunnelIcon/>},
              {id:"cuisines",label:"Cuisines",active:!!filterCuisine,icon:<FunnelIcon/>},
              {id:"tags",label:"Tags",active:filterTags.length>0,count:filterTags.length||null,icon:<FunnelIcon/>},
              {id:"sort",label:"Sort",active:sortBy!=="name",icon:<SortIcon/>},
            ].map(({id,label,active,count,icon})=>(
              <button key={id} onClick={()=>setOpenPill(openPill===id?null:id)} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"6px 14px",borderRadius:20,border:`1.5px solid ${active||openPill===id?BRAND:BORDER}`,background:active?BRAND:openPill===id?BRAND_LIGHT:WHITE,color:active?WHITE:openPill===id?BRAND:TEXT_MID,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:SANS,whiteSpace:"nowrap",lineHeight:1}}>
                {icon}{label}{count?` (${count})`:null}
              </button>
            ))}
          </div>
          {openPill==="towns"&&(
            <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:6}}>
              <button onClick={()=>{setFilterTown("");setOpenPill(null);}} style={{padding:"5px 12px",borderRadius:20,border:`1.5px solid ${!filterTown?BRAND:BORDER}`,background:!filterTown?BRAND:WHITE,color:!filterTown?WHITE:TEXT_MID,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:SANS}}>All</button>
              {allTowns.map(t=><button key={t} onClick={()=>{setFilterTown(t);setOpenPill(null);}} style={{padding:"5px 12px",borderRadius:20,border:`1.5px solid ${filterTown===t?BRAND:BORDER}`,background:filterTown===t?BRAND:WHITE,color:filterTown===t?WHITE:TEXT_MID,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:SANS}}>{t}</button>)}
            </div>
          )}
          {openPill==="cuisines"&&(
            <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:6}}>
              <button onClick={()=>{setFilterCuisine("");setOpenPill(null);}} style={{padding:"5px 12px",borderRadius:20,border:`1.5px solid ${!filterCuisine?BRAND:BORDER}`,background:!filterCuisine?BRAND:WHITE,color:!filterCuisine?WHITE:TEXT_MID,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:SANS}}>All</button>
              {allCuisines.map(c=><button key={c} onClick={()=>{setFilterCuisine(c);setOpenPill(null);}} style={{padding:"5px 12px",borderRadius:20,border:`1.5px solid ${filterCuisine===c?BRAND:BORDER}`,background:filterCuisine===c?BRAND:WHITE,color:filterCuisine===c?WHITE:TEXT_MID,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:SANS}}>{c}</button>)}
            </div>
          )}
          {openPill==="tags"&&(
            <div style={{marginTop:8}}>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{DEFAULT_TAGS.map(t=><TagPill key={t} label={t} selected={filterTags.includes(t)} onClick={()=>toggleTag(t)}/>)}</div>
              {filterTags.length>0&&<button onClick={()=>setFilterTags([])} style={{marginTop:8,background:"none",border:"none",color:BRAND,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:SANS,padding:0}}>Clear all ×</button>}
            </div>
          )}
          {openPill==="sort"&&(
            <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:2}}>
              {[{v:"name",l:"A–Z"},{v:"pj",l:"PJ's Rating"},{v:"members",l:"Members' Rating"},{v:"distance",l:`Nearest to Me${userTown?` (from ${userTown})`:""}`}].map(({v,l})=>(
                <button key={v} onClick={()=>{handleSort(v);setOpenPill(null);}} style={{textAlign:"left",padding:"8px 12px",borderRadius:8,border:"none",background:sortBy===v?BRAND_LIGHT:"none",color:sortBy===v?BRAND:TEXT,fontSize:13,fontWeight:sortBy===v?700:400,cursor:"pointer",fontFamily:SANS}}>{l}</button>
              ))}
            </div>
          )}
        </div>
        <div ref={azRef} style={{position:"fixed",right:0,top:"50%",transform:"translateY(-50%)",zIndex:50,display:"flex",flexDirection:"column",alignItems:"center",background:"rgba(255,255,255,0.9)",borderRadius:"10px 0 0 10px",padding:"4px 3px",boxShadow:"-2px 0 8px rgba(0,0,0,0.08)",opacity:0,transition:"opacity 0.2s"}}
          onMouseEnter={azShow} onMouseLeave={()=>azHide(0)}
          onTouchStart={()=>{azShow();azHide(2000);}}
          onTouchMove={e=>{e.preventDefault();azShow();azHide(2000);const t=e.touches[0];const el=document.elementFromPoint(t.clientX,t.clientY);if(el?.dataset?.letter)scrollToLetter(el.dataset.letter);}}
        >
          {ALPHA.map(l=>(
            <button key={l} data-letter={l} onClick={()=>scrollToLetter(l)} style={{display:"block",padding:"1px 5px",background:"none",border:"none",fontSize:9,fontWeight:700,cursor:letterFirstId[l]?"pointer":"default",color:letterFirstId[l]?BRAND:TEXT_LIGHT,fontFamily:SANS,lineHeight:1.5,opacity:letterFirstId[l]?1:0.3,userSelect:"none"}}>{l}</button>
          ))}
        </div>
        {list.length===0&&<div style={{color:TEXT_LIGHT,textAlign:"center",marginTop:48,fontSize:14}}>No restaurants found.</div>}
        {list.map(r=>{
          const pj=pjR(r.id); const jess=jessR(r.id); const revs=getRevs(r.id); const avg=avgRating(revs);
          const dist=sortBy==="distance"&&userTown?townDist(userTown,r.town).toFixed(1)+" mi":null;
          return(
            <div key={r.id} id={`card-${r.id}`} style={{...T.card,cursor:"pointer"}} onClick={()=>setSelRest(r)}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:16,color:TEXT,marginBottom:2}}>{r.name}</div>
                  <div style={{fontSize:11,color:TEXT_LIGHT,textTransform:"uppercase",letterSpacing:"0.07em"}}>{r.town} · {r.cuisine}{dist?` · ${dist}`:""}</div>
                  {(r.tags||[]).length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:6}}>{(r.tags||[]).map(t=><span key={t} style={{background:BRAND_LIGHT,color:BRAND,fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,letterSpacing:"0.03em"}}>{t}</span>)}</div>}
                </div>
                <div style={{flexShrink:0,display:"flex",flexDirection:"column",gap:5,alignItems:"flex-end"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:10,fontWeight:700,color:TEXT_MID,whiteSpace:"nowrap"}}>PJ Says:</span>
                    {pj.rating>0?<Stars n={pj.rating} size={18}/>:<span style={{fontSize:11,color:TEXT_LIGHT}}>—</span>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:10,fontWeight:700,color:TEXT_MID,whiteSpace:"nowrap"}}>Jessica Says:</span>
                    {jess.rating>0?<Stars n={jess.rating} size={18}/>:<span style={{fontSize:11,color:TEXT_LIGHT}}>—</span>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:10,fontWeight:700,color:TEXT_MID,whiteSpace:"nowrap"}}>Members Say:</span>
                    {avg>0?<Stars n={avg} size={18}/>:<span style={{fontSize:11,color:TEXT_LIGHT}}>—</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {user.isAdmin&&<button style={{position:"fixed",bottom:140,right:18,width:52,height:52,borderRadius:"50%",background:BRAND,border:"none",color:WHITE,fontSize:28,cursor:"pointer",boxShadow:`0 4px 16px rgba(107,39,55,0.4)`,zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:SANS,lineHeight:1}} onClick={()=>setShowAdd(true)}>+</button>}
      </div>
    );
  };

  // ── RESTAURANT DETAIL ────────────────────────────────────────────────
  const RestaurantDetail=()=>{
    const r=selRest; const pjR=pjReviews[r.id]||{rating:0,review:""};
    const pubRevs=userReviews.filter(x=>x.restaurantId===r.id&&x.isPublic&&x.userId!==user.id);
    const [revSort,setRevSort]=useState("rating");
    const sorted=[...pubRevs].sort((a,b)=>revSort==="rating"?b.rating-a.rating:a.name.localeCompare(b.name));
    const links=[{label:"Website",url:r.website},{label:"Menu",url:r.menu},{label:"Reserve",url:r.reservation},{label:"Order Online",url:r.orderOnline}].filter(l=>l.url);
    return(
      <div style={{paddingBottom:100,fontFamily:SANS}}>
        <div style={{background:WHITE,padding:"20px 18px",borderBottom:`1px solid ${BORDER}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <button style={{background:"none",border:"none",color:BRAND,fontSize:14,cursor:"pointer",padding:0,fontWeight:700,fontFamily:SANS}} onClick={()=>setSelRest(null)}>← Back</button>
            {user.isAdmin&&<button onClick={()=>{setEditRest(r);setSelRest(null);}} style={{background:BRAND_LIGHT,border:`1px solid ${BRAND}33`,borderRadius:6,padding:"6px 14px",color:BRAND,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:SANS}}>✏️ Edit</button>}
          </div>
          <div style={{fontWeight:800,fontSize:26,color:TEXT,lineHeight:1.1,marginBottom:4}}>{r.name}</div>
          <div style={{fontSize:11,color:TEXT_LIGHT,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{r.cuisine} · {r.town}</div>
          {(r.tags||[]).length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:10}}>{(r.tags||[]).map(t=><span key={t} style={{background:BRAND_LIGHT,color:BRAND,fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:12}}>{t}</span>)}</div>}
          {r.address&&<a href={`https://maps.apple.com/?q=${encodeURIComponent(r.address)}`} style={{fontSize:13,color:BRAND,marginBottom:4,display:"block",textDecoration:"none",fontWeight:500}}>📍 {r.address}</a>}
          {r.phone&&<a href={`tel:${r.phone.replace(/\D/g,"")}`} style={{fontSize:13,color:BRAND,marginBottom:10,display:"block",textDecoration:"none",fontWeight:500}}>📞 {r.phone}</a>}
          {links.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:7,marginTop:8}}>{links.map(l=><a key={l.label} href={l.url} target="_blank" rel="noreferrer" style={{background:BRAND_LIGHT,border:`1px solid ${BRAND}22`,borderRadius:20,padding:"6px 14px",color:BRAND,fontSize:12,textDecoration:"none",fontWeight:600}}>{l.label}</a>)}</div>}
        </div>
        <div style={T.card}><Label>PJ's Rating</Label><InlineRating restaurantId={r.id} who="pj"/></div>
        <div style={T.card}><Label>Jessica's Rating</Label><InlineRating restaurantId={r.id} who="jessica"/></div>
        <div style={T.card}><Label>PJ's & Jessica's Notes</Label><AdminNotes restaurantId={r.id}/></div>
        {!user.isAdmin&&<div style={T.card}><Label>My Rating</Label><InlineRating restaurantId={r.id}/></div>}
        <div style={T.card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <Label style={{marginBottom:0}}>Members' Ratings and Reviews</Label>
            <select style={{...T.sel,width:"auto",fontSize:11,padding:"4px 8px"}} value={revSort} onChange={e=>setRevSort(e.target.value)}>
              <option value="rating">Best First</option><option value="name">A–Z</option>
            </select>
          </div>
          {sorted.length===0&&<div style={{color:TEXT_LIGHT,fontSize:13}}>No public reviews yet.</div>}
          {sorted.map(rev=>(
            <div key={rev.id} style={{background:CREAM,borderRadius:8,padding:"12px 14px",marginTop:8,border:`1px solid ${BORDER_LIGHT}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:rev.review?4:0}}>
                <span style={{fontWeight:700,fontSize:14,color:TEXT}}>{rev.name}</span><Stars n={rev.rating} size={14}/>
              </div>
              {rev.review&&<div style={{fontSize:13,color:TEXT_MID,fontStyle:"italic",lineHeight:1.5}}>"{rev.review}"</div>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── MEMBERS TAB ──────────────────────────────────────────────────────
  const MembersTab=()=>{
    const [followOnly,setFollowOnly]=useState(false); const [memberSort,setMemberSort]=useState("name");
    const toggleFav=async uid=>{
      if(favIds.includes(uid)){
        setFavIds(p=>p.filter(x=>x!==uid));
        await supabase.from("favorite_members").delete().eq("user_id",user.id).eq("favorited_user_id",uid);
      } else {
        setFavIds(p=>[...p,uid]);
        await supabase.from("favorite_members").insert({user_id:user.id,favorited_user_id:uid});
      }
    };
    const pubCount=m=>m.isAdmin?Object.values(pjReviews).filter(r=>r.review&&r.review.trim()).length:userReviews.filter(r=>r.userId===m.id&&r.isPublic).length;
    let members=users.filter(u=>u.approved&&u.id!==user.id);
    if(followOnly)members=members.filter(u=>favIds.includes(u.id));
    members=[...members].sort((a,b)=>memberSort==="reviews"?pubCount(b)-pubCount(a):a.name.localeCompare(b.name));
    return(
      <div style={{paddingBottom:160,fontFamily:SANS}}>
        <div style={{padding:"20px 16px 10px"}}><div style={{fontWeight:800,fontSize:22,color:TEXT}}>Members</div><div style={{fontSize:13,color:TEXT_LIGHT,marginTop:2}}>Tap a member to see their public reviews.</div></div>
        <div style={{display:"flex",gap:8,padding:"4px 14px 10px",alignItems:"center"}}>
          <button onClick={()=>setFollowOnly(!followOnly)} style={{background:followOnly?BRAND:"none",border:`1.5px solid ${followOnly?BRAND:BORDER}`,borderRadius:20,padding:"8px 20px",color:followOnly?WHITE:TEXT_MID,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:SANS,whiteSpace:"nowrap",flexShrink:0}}>{followOnly?"★ Following":"☆ Following"}</button>
          <select style={{...T.sel,width:130,flexShrink:0}} value={memberSort} onChange={e=>setMemberSort(e.target.value)}><option value="name">A–Z</option><option value="reviews">Most Reviews</option></select>
        </div>
        {members.length===0&&<div style={{color:TEXT_LIGHT,textAlign:"center",marginTop:40,fontSize:14}}>No members found.</div>}
        {members.map(m=>{
          const cnt=pubCount(m); const isFav=favIds.includes(m.id);
          return(<div key={m.id} style={{...T.card,display:"flex",alignItems:"center",gap:12}}>
            <div style={{flex:1,cursor:"pointer"}} onClick={()=>setSelUser(m)}>
              <div style={{fontWeight:700,fontSize:16,color:TEXT}}>{m.name}{m.isAdmin&&<span style={{fontSize:10,color:BRAND,marginLeft:6,fontWeight:700,textTransform:"uppercase",background:BRAND_LIGHT,padding:"2px 7px",borderRadius:10}}>Admin</span>}</div>
              <div style={{fontSize:12,color:TEXT_LIGHT,marginTop:2}}>{cnt} public {cnt===1?"review":"reviews"}</div>
            </div>
            <button onClick={()=>toggleFav(m.id)} style={{background:"none",border:`1.5px solid ${isFav?BRAND:BORDER}`,borderRadius:20,width:36,height:36,cursor:"pointer",color:isFav?BRAND:TEXT_LIGHT,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{isFav?"★":"☆"}</button>
          </div>);
        })}
      </div>
    );
  };

  // ── USER PROFILE VIEW ────────────────────────────────────────────────
  const UserProfileView=()=>{
    const u=selUser; const revs=userReviews.filter(r=>r.userId===u.id&&r.isPublic);
    const displayCount=u.isAdmin?Object.values(pjReviews).filter(r=>r.review&&r.review.trim()).length:revs.length;
    const firstName=u.name.split(" ")[0]; const [revSort,setRevSort]=useState("rating");
    const adminNotesList=u.isAdmin?restaurants.filter(r=>pjReviews[r.id]?.review?.trim()).map(r=>({rest:r,note:pjReviews[r.id].review})).sort((a,b)=>a.rest.name.localeCompare(b.rest.name)):null;
    const sorted=[...revs].sort((a,b)=>revSort==="rating"?b.rating-a.rating:(restaurants.find(r=>r.id===a.restaurantId)?.name||"").localeCompare(restaurants.find(r=>r.id===b.restaurantId)?.name||""));
    return(
      <div style={{paddingBottom:100,fontFamily:SANS}}>
        <div style={{background:WHITE,padding:"20px 18px",borderBottom:`1px solid ${BORDER}`}}>
          <button style={{background:"none",border:"none",color:BRAND,fontSize:14,cursor:"pointer",padding:"0 0 12px",fontWeight:700,fontFamily:SANS}} onClick={()=>setSelUser(null)}>← Back</button>
          <div style={{fontWeight:800,fontSize:26,color:TEXT}}>{u.name}</div>
          <div style={{fontSize:11,color:TEXT_LIGHT,textTransform:"uppercase",letterSpacing:"0.07em",marginTop:3}}>{displayCount} public {displayCount===1?"review":"reviews"}</div>
        </div>
        {!u.isAdmin&&revs.length>0&&<div style={{padding:"10px 14px 0"}}><select style={T.sel} value={revSort} onChange={e=>setRevSort(e.target.value)}><option value="rating">Sort: {firstName}'s Rating (Best First)</option><option value="name">Sort: Restaurant A–Z</option></select></div>}
        <div style={{padding:"8px 0 80px"}}>
          {u.isAdmin?(
            adminNotesList.length===0
              ?<div style={{color:TEXT_LIGHT,fontSize:14,padding:"28px 20px"}}>No notes yet.</div>
              :adminNotesList.map(({rest,note})=>(
                <div key={rest.id} style={T.card}>
                  <div style={{fontWeight:700,fontSize:16,color:TEXT,marginBottom:2}}>{rest.name}</div>
                  <div style={{fontSize:11,color:TEXT_LIGHT,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>{rest.town} · {rest.cuisine}</div>
                  <div style={{fontSize:13,color:TEXT_MID,fontStyle:"italic",lineHeight:1.6}}>"{note}"</div>
                </div>
              ))
          ):(
            <>
              {sorted.length===0&&<div style={{color:TEXT_LIGHT,fontSize:14,padding:"28px 20px"}}>No public reviews yet.</div>}
              {sorted.map(rev=>{const rest=restaurants.find(r=>r.id===rev.restaurantId);return(
                <div key={rev.id} style={T.card}>
                  <div style={{fontWeight:700,fontSize:16,color:TEXT,marginBottom:2}}>{rest?.name||"Unknown"}</div>
                  <div style={{fontSize:11,color:TEXT_LIGHT,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>{rest?.town} · {rest?.cuisine}</div>
                  <Stars n={rev.rating} size={18}/>
                  {rev.review&&<div style={{fontSize:13,color:TEXT_MID,fontStyle:"italic",lineHeight:1.6,marginTop:6}}>"{rev.review}"</div>}
                </div>
              );})}
            </>
          )}
        </div>
      </div>
    );
  };

  // ── ADMIN TAB ────────────────────────────────────────────────────────
  const AdminTab=()=>{
    const [newU,setNewU]=useState({name:"",username:"",password:"",email:""});
    const [confirmRemove,setConfirmRemove]=useState(null);
    const [approveMsg,setApproveMsg]=useState(null);
    const [busy,setBusy]=useState(false);
    const approve=async req=>{
      setBusy(true);
      const {data,error}=await supabase.functions.invoke("approve-user",{body:{name:req.name,email:req.email,requestId:req.id}});
      if(error||!data?.userId){console.error("approve-user error:",error||data);setBusy(false);return;}
      await supabase.from("pending_requests").delete().eq("id",req.id);
      setPending(p=>p.filter(r=>r.id!==req.id));
      setUsers(p=>[...p,{id:data.userId,username:data.username,name:req.name,email:req.email,isAdmin:false,approved:true}]);
      setApproveMsg(req.name);
      setBusy(false);
    };
    const deny=async req=>{
      setPending(p=>p.filter(r=>r.id!==req.id));
      await supabase.from("pending_requests").delete().eq("id",req.id);
    };
    const addUser=async()=>{
      if(!newU.username||!newU.password||!newU.email)return;
      setBusy(true);
      const {data,error}=await supabase.functions.invoke("create-user",{body:{name:newU.name,email:newU.email,username:newU.username,password:newU.password}});
      if(!error&&data?.userId){
        setUsers(p=>[...p,{id:data.userId,username:newU.username,name:newU.name,email:newU.email,isAdmin:false,approved:true}]);
        setNewU({name:"",username:"",password:"",email:""});
      }
      setBusy(false);
    };
    const removeUser=async id=>{
      setUsers(p=>p.filter(u=>u.id!==id));
      setConfirmRemove(null);
      const {data:{session}}=await supabase.auth.getSession();
      const token=session?.access_token??supabaseAnonKey;
      const {error}=await supabase.functions.invoke("delete-user",{body:{userId:id},headers:{Authorization:`Bearer ${token}`}});
      if(error){let msg=error.message;try{const b=await error.context.json();msg=b.error||b.message||msg;}catch(e){}console.error("delete-user error:",msg);alert("Failed to remove user: "+msg);}
    };
    return(
      <div style={{paddingBottom:160,fontFamily:SANS}}>
        {confirmRemove&&<Modal onClose={()=>setConfirmRemove(null)}><div style={{fontWeight:800,fontSize:17,marginBottom:8,color:TEXT}}>Remove Member?</div><div style={{fontSize:14,color:TEXT_MID,marginBottom:20,lineHeight:1.6}}>Are you sure you want to remove <strong>{confirmRemove.name}</strong>?</div><div style={{display:"flex",gap:10}}><button style={{...T.btnP,background:"#C0392B",flex:1,boxShadow:"none"}} onClick={()=>removeUser(confirmRemove.id)}>Remove</button><button style={{...T.btnO,flex:1,marginTop:0}} onClick={()=>setConfirmRemove(null)}>Cancel</button></div></Modal>}
        {approveMsg&&<Modal onClose={()=>setApproveMsg(null)}>
          <div style={{fontWeight:800,fontSize:17,marginBottom:8,color:TEXT}}>✓ {approveMsg} Approved</div>
          <div style={{fontSize:14,color:TEXT_MID,marginBottom:20,lineHeight:1.6}}>An invite email has been sent. They'll set their own password via the link.</div>
          <button style={T.btnP} onClick={()=>setApproveMsg(null)}>Done</button>
        </Modal>}
        <div style={{padding:"20px 16px 10px"}}><div style={{fontWeight:800,fontSize:22,color:TEXT}}>Admin Panel</div></div>
        <div style={T.card}><Label>Share The Regular</Label><div style={{fontSize:13,color:TEXT_MID,marginBottom:12,lineHeight:1.5}}>New users must request access and be approved before joining.</div><div style={{display:"flex",gap:10}}><a href={`sms:?body=Join me on The Regular: ${shareUrl}`} style={{flex:1,background:BRAND_LIGHT,border:`1px solid ${BRAND}33`,borderRadius:8,padding:"10px",color:BRAND,textDecoration:"none",fontSize:13,fontWeight:700,textAlign:"center",display:"block"}}>📱 Text</a><a href={`mailto:?subject=Join me on The Regular&body=Request access here: ${shareUrl}`} style={{flex:1,background:BRAND_LIGHT,border:`1px solid ${BRAND}33`,borderRadius:8,padding:"10px",color:BRAND,textDecoration:"none",fontSize:13,fontWeight:700,textAlign:"center",display:"block"}}>✉️ Email</a></div></div>
        <div style={T.card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><Label style={{marginBottom:0}}>Pending Requests</Label>{pending.length>0&&<span style={{background:BRAND,color:WHITE,borderRadius:"50%",width:22,height:22,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800}}>{pending.length}</span>}</div>
          {pending.length===0&&<div style={{color:TEXT_LIGHT,fontSize:13}}>No pending requests.</div>}
          {pending.map(req=>(<div key={req.id} style={{background:CREAM,borderRadius:8,padding:"12px 14px",marginBottom:8,border:`1px solid ${BORDER_LIGHT}`}}><div style={{fontWeight:700,color:TEXT}}>{req.name}</div><div style={{fontSize:12,color:TEXT_LIGHT,marginTop:2}}>{req.email} · {req.request_date}</div>{req.note&&<div style={{fontSize:13,color:TEXT_MID,fontStyle:"italic",marginTop:4}}>"{req.note}"</div>}<div style={{display:"flex",gap:8,marginTop:10}}><button style={{...T.btnP,width:"auto",padding:"7px 16px",fontSize:13,background:GREEN,boxShadow:"none"}} onClick={()=>approve(req)} disabled={busy}>Approve</button><button style={{...T.btnP,width:"auto",padding:"7px 16px",fontSize:13,background:"#C0392B",boxShadow:"none"}} onClick={()=>deny(req)}>Deny</button></div></div>))}
        </div>
        <div style={T.card}><Label>Add User Directly</Label>{[["name","Full Name"],["email","Email Address"],["username","Username"],["password","Temporary Password"]].map(([k,pl])=>(<div key={k}><Label>{pl}</Label><input style={T.inp} placeholder={pl} value={newU[k]} onChange={e=>setNewU(p=>({...p,[k]:k==="username"?e.target.value.toLowerCase():e.target.value}))} autoCapitalize="none"/></div>))}<button style={T.btnP} onClick={addUser} disabled={busy}>{busy?"Adding…":"Add User"}</button></div>
        <div style={T.card}><Label>All Members ({users.filter(u=>u.approved).length})</Label>{users.filter(u=>u.approved).map(u=>(<div key={u.id} style={{borderBottom:`1px solid ${BORDER_LIGHT}`,padding:"10px 0",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}><div><div style={{fontWeight:700,color:TEXT,fontSize:14}}>{u.name}{u.isAdmin&&<span style={{fontSize:10,color:BRAND,marginLeft:6,fontWeight:700,textTransform:"uppercase",background:BRAND_LIGHT,padding:"2px 7px",borderRadius:10}}>Admin</span>}</div><div style={{fontSize:12,color:TEXT_LIGHT,marginTop:2}}>{u.email}</div></div>{!u.isAdmin&&<button onClick={()=>setConfirmRemove(u)} style={{background:"none",border:`1px solid #E0D0D0`,borderRadius:6,color:"#C0392B",fontSize:12,cursor:"pointer",padding:"4px 10px",fontWeight:600,fontFamily:SANS,flexShrink:0,marginLeft:12}}>Remove</button>}</div>))}</div>
      </div>
    );
  };

  // ── RATING LEGEND ────────────────────────────────────────────────────
  const LegendBar=()=>(
    <div style={{background:WHITE,borderTop:`1px solid ${BORDER}`,padding:"8px 16px 6px"}}>
      <div style={{display:"flex",justifyContent:"space-around",alignItems:"center"}}>
        {[1,2,3].map(n=>(
          <div key={n} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
            <Stars n={n} size={13}/>
            <span style={{fontSize:9,color:TEXT_MID,fontWeight:600,textAlign:"center",lineHeight:1.2,letterSpacing:"0.02em"}}>{PJ_LABELS[n]}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // ── SHELL ────────────────────────────────────────────────────────────
  if(appLoading)return(
    <div style={{minHeight:"100vh",background:CREAM,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:SANS}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:36,fontWeight:800,color:BRAND,marginBottom:12}}>The Regular</div>
        <div style={{color:TEXT_LIGHT,fontSize:14}}>Loading…</div>
      </div>
    </div>
  );

  if(screen==="login")return <LoginScreen/>;
  if(screen==="request")return <RequestScreen/>;

  const showDetail=selRest||selUser||showAdd||editRest||showProfile;

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100dvh",maxWidth:430,margin:"0 auto",background:CREAM,color:TEXT,fontFamily:SANS,position:"relative",overflow:"hidden"}}>
      {resetModal&&<Modal onClose={()=>{}}>
        <div style={{fontWeight:800,fontSize:17,marginBottom:8,color:TEXT}}>Set New Password</div>
        <div style={{fontSize:14,color:TEXT_MID,marginBottom:16,lineHeight:1.5}}>Choose a new password for your account.</div>
        <input style={T.inp} type="password" placeholder="Minimum 6 characters" value={newPassword} onChange={e=>{setNewPassword(e.target.value);setResetPwErr("");}} autoFocus/>
        {resetPwErr&&<div style={{color:"#C0392B",fontSize:13,marginBottom:10,padding:"8px 12px",background:"#FDF2F2",borderRadius:6}}>{resetPwErr}</div>}
        <button style={T.btnP} onClick={async()=>{if(newPassword.length<6){setResetPwErr("Password must be at least 6 characters.");return;}const {error}=await supabase.auth.updateUser({password:newPassword});if(error){setResetPwErr(error.message);return;}setResetModal(false);setNewPassword("");setResetPwErr("");}}>Save Password</button>
      </Modal>}
      <div style={{background:WHITE,padding:"13px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`1px solid ${BORDER}`,boxShadow:"0 1px 4px rgba(0,0,0,0.06)",flexShrink:0,position:"relative"}}>
        <div style={{width:40}}/>
        <span style={{fontWeight:800,fontSize:24,color:BRAND,letterSpacing:"0.01em",position:"absolute",left:"50%",transform:"translateX(-50%)"}}>The Regular</span>
        <button onClick={()=>setShowProfile(true)} style={{background:BRAND_LIGHT,border:`1.5px solid ${BRAND}22`,borderRadius:"50%",width:38,height:38,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,padding:0}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill={BRAND}><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
        </button>
      </div>

      {tab==="restaurants"&&!showDetail&&(
        <div style={{background:WHITE,borderBottom:`1px solid ${BORDER}`,padding:"8px 14px",flexShrink:0}}>
          <div style={{position:"relative"}}>
            <input ref={searchRef} value={filterSearch} onChange={e=>setFilterSearch(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")e.target.blur();}} placeholder="Search restaurants…" style={{width:"100%",boxSizing:"border-box",padding:"8px 32px 8px 12px",borderRadius:10,border:`1.5px solid ${filterSearch?BRAND:BORDER}`,fontSize:16,fontFamily:SANS,color:TEXT,background:WHITE,outline:"none",touchAction:"manipulation"}}/>
            {filterSearch&&<button onClick={()=>{setFilterSearch("");searchRef.current?.blur();}} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:TEXT_LIGHT,fontSize:16,cursor:"pointer",lineHeight:1,padding:0}}>×</button>}
          </div>
        </div>
      )}
      <div style={{flex:1,overflowY:"auto"}}>
        {showProfile?<ProfilePage/>:
         editRest?<RestaurantForm
           isEdit initial={editRest}
           allCuisines={allCuisines} customTowns={customTowns} addCustomTown={addCustomTown} addCustomCuisine={addCustomCuisine}
           onSave={async form=>{
             const updated={...editRest,...form};
             setRestaurants(p=>p.map(r=>r.id===editRest.id?updated:r));
             setSelRest(updated);setEditRest(null);
             await supabase.from("restaurants").update(toDbRestaurant(updated)).eq("id",editRest.id);
           }}
           onCancel={()=>setEditRest(null)}
           onDelete={async()=>{
             const id=editRest.id;
             setRestaurants(p=>p.filter(r=>r.id!==id));
             setPjReviews(p=>{const n={...p};delete n[id];return n;});
             setJessicaReviews(p=>{const n={...p};delete n[id];return n;});
             setUserReviews(p=>p.filter(r=>r.restaurantId!==id));
             setEditRest(null);
             await supabase.from("restaurants").delete().eq("id",id);
           }}
         />:
         showAdd?<RestaurantForm
           allCuisines={allCuisines} customTowns={customTowns} addCustomTown={addCustomTown} addCustomCuisine={addCustomCuisine}
           onSave={async form=>{
             const newRest={id:`r_${Date.now()}`,...form,addedBy:user.username};
             setRestaurants(p=>[...p,newRest]);setShowAdd(false);
             await supabase.from("restaurants").insert(toDbRestaurant(newRest,user.username));
           }}
           onCancel={()=>setShowAdd(false)}
         />:
         selRest?<RestaurantDetail/>:selUser?<UserProfileView/>:
         tab==="restaurants"?<RestaurantList/>:tab==="members"?<MembersTab/>:<AdminTab/>}
      </div>

      {!showDetail&&(
        <>
          <LegendBar/>
          <div style={{display:"flex",background:WHITE,borderTop:`1px solid ${BORDER}`,flexShrink:0}}>
            {[["restaurants","🍽️","Restaurants"],["members","👥","Members"],...(user.isAdmin?[["admin","⚙️","Admin"]]:[])].map(([t,icon,label])=>{
              const active=tab===t;
              return(
                <button key={t} onClick={()=>setTab(t)} style={{flex:1,background:"none",border:"none",padding:"8px 0 8px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,fontFamily:SANS,position:"relative"}}>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,background:active?BRAND_LIGHT:"none",borderRadius:12,padding:"5px 14px",transition:"background 0.15s"}}>
                    <span style={{fontSize:18}}>{icon}</span>
                    <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:active?BRAND:TEXT_LIGHT}}>{label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
